import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CircularProgress from '@mui/material/CircularProgress';

import axios from 'axios';
import { API_URL } from '../../../links/API_LINKS';

import { Auth } from 'aws-amplify';

import { connect } from 'react-redux';
import { signIn, signOut, fetchPartnerDetails } from '../../../redux';

import ResetForm from '../resetPassword/ResetForm';
import ConfirmationForm from '../resetPassword/ConfirmationForm';

// ----------------------------------------------------------------------

const LoginForm = ({
  fetchPartnerDetails,
  currentAuthStatus,
  partner,
  signIn,
  signOut,
}) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState();
  const [processing, setProcessing] = useState(true);

  const [hideLogin, setHideLogin] = useState(true);
  const [nextLoading, setNextLoading] = useState(false);

  const [resetPage, setResetPage] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [enteredEmail, setEnteredEmail] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    if (!partner.loading) {
      if (currentAuthStatus && !partner.partner) {
        alert('Unauthorized to access this dashboard');
        Auth.signOut();
        signOut();
        localStorage.removeItem('authorization');
        navigate('/', { replace: true });
      } else if (currentAuthStatus) {
        navigate('/dashboard/app', { replace: true });
      } else {
        Auth.currentAuthenticatedUser({
          bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
        })
          .then((user) => {
            axios.defaults.headers.common['Authorization'] =
              user.signInUserSession.accessToken.jwtToken;
            fetchPartnerDetails();
            signIn();
          })
          .catch(() => {
            console.log('Not signed in');
            setProcessing(false);
          });
      }
    }
  }, [
    currentAuthStatus,
    signIn,
    signOut,
    fetchPartnerDetails,
    navigate,
    partner,
  ]);

  async function awsSignIn(username, password) {
    try {
      const user = await Auth.signIn(username, password);
      localStorage.setItem(
        'authorization',
        user.signInUserSession.accessToken.jwtToken
      );
      axios.defaults.headers.common['Authorization'] =
        user.signInUserSession.accessToken.jwtToken;
      fetchPartnerDetails();
      if (!currentAuthStatus) {
        signIn();
      }
    } catch (error) {
      setFeedbackMessage(error.message);
      setSubmitting(false);
    }
  }

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('Email must be a valid email address')
      .required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: true,
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      awsSignIn(values.email, values.password);
    },
  });

  const {
    errors,
    touched,
    values,
    isSubmitting,
    setSubmitting,
    handleSubmit,
    getFieldProps,
  } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };
  // console.log(values);

  async function checkEmail() {
    try {
      setNextLoading(true);
      setEnteredEmail(values.email);
      const result = await axios(API_URL + '/admin/checkProfileStage', {
        params: {
          email: values.email,
        },
      });
      setNextLoading(false);
      if (!result.data.data) {
        setFeedbackMessage('User not found');
      } else if (result.data.data.passwordResetRequired) {
        setUserId(result.data.data._id);
        await Auth.forgotPassword(values.email);
        setCodeSent(true);
        setResetPage(true);
      } else {
        setHideLogin(false);
      }
    } catch (error) {
      setFeedbackMessage(error.message);
      setSubmitting(false);
      setNextLoading(false);
    }
  }

  return (
    <>
      {processing ? (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight='50vh'
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {resetPage ? (
            !codeSent ? (
              <ResetForm
                setCodeSent={setCodeSent}
                setEnteredEmail={setEnteredEmail}
                setResetPage={setResetPage}
              />
            ) : !confirmed ? (
              <ConfirmationForm
                enteredEmail={enteredEmail}
                setConfirmed={setConfirmed}
                userId={userId}
              />
            ) : (
              <>
                <Stack sx={{ mb: 5 }}>
                  <Typography variant='h4' gutterBottom>
                    Password Successfully Changed!
                  </Typography>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                    style={{
                      marginTop: '10px',
                      display: 'flex',
                      justifyContent: 'right',
                      cursor: 'pointer',
                    }}
                    onClick={() => setResetPage(false)}
                  >
                    Back
                  </Typography>
                </Stack>
              </>
            )
          ) : (
            <>
              <Stack sx={{ mb: 5 }}>
                <Typography variant='h4' gutterBottom>
                  Sign in to Belong
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Enter your details below.
                </Typography>
              </Stack>
              <FormikProvider value={formik}>
                <Form autoComplete='off' noValidate onSubmit={handleSubmit}>
                  <Stack spacing={3} sx={{ my: 2 }}>
                    <TextField
                      fullWidth
                      autoComplete='username'
                      type='email'
                      label='Email address'
                      {...getFieldProps('email')}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Stack>
                  {!hideLogin && (
                    <>
                      {' '}
                      <Stack spacing={3} sx={{ my: 2 }}>
                        <TextField
                          fullWidth
                          autoComplete='current-password'
                          type={showPassword ? 'text' : 'password'}
                          label='Password'
                          {...getFieldProps('password')}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position='end'>
                                <IconButton
                                  onClick={handleShowPassword}
                                  edge='end'
                                >
                                  <Icon
                                    icon={showPassword ? eyeFill : eyeOffFill}
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          error={Boolean(touched.password && errors.password)}
                          helperText={touched.password && errors.password}
                        />
                      </Stack>
                      <LoadingButton
                        fullWidth
                        size='large'
                        type='submit'
                        variant='contained'
                        loading={isSubmitting}
                      >
                        Login
                      </LoadingButton>{' '}
                    </>
                  )}
                </Form>
                <Stack>{feedbackMessage}</Stack>
              </FormikProvider>
              {hideLogin && (
                <LoadingButton
                  fullWidth
                  size='large'
                  type='submit'
                  variant='contained'
                  onClick={() => checkEmail()}
                  loading={nextLoading}
                >
                  Next
                </LoadingButton>
              )}
              <Typography
                variant='subtitle2'
                gutterBottom
                style={{
                  marginTop: '10px',
                  display: 'flex',
                  justifyContent: 'right',
                  cursor: 'pointer',
                }}
                onClick={() => setResetPage(true)}
              >
                Forgot Password?
              </Typography>
            </>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    currentAuthStatus: state.auth.authStatus,
    partner: state.partner,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    signIn: () => dispatch(signIn()),
    signOut: () => dispatch(signOut()),
    fetchPartnerDetails: () => dispatch(fetchPartnerDetails()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
